import { type ReactNode, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

const VARIANT_STYLE: Record<
  AlertVariant,
  { icon: typeof Info; bg: string; border: string; fg: string; iconColor: string }
> = {
  info: { icon: Info, bg: '#0073EA14', border: '#0073EA40', fg: '#1A202C', iconColor: '#0073EA' },
  success: { icon: CheckCircle2, bg: '#00C87514', border: '#00C87540', fg: '#1A202C', iconColor: '#00A862' },
  warning: { icon: AlertTriangle, bg: '#F973161A', border: '#F9731655', fg: '#1A202C', iconColor: '#D97706' },
  error: { icon: XCircle, bg: '#E2445C14', border: '#E2445C40', fg: '#1A202C', iconColor: '#E2445C' },
};

export interface AlertBannerProps {
  variant?: AlertVariant;
  title?: string;
  children?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Action optionnelle (ex : bouton « Voir »). */
  action?: ReactNode;
  className?: string;
}

/**
 * Bannière d'alerte : info / success / warning / error, dismissable,
 * action optionnelle. Brief — composant #10.
 */
export function AlertBanner({
  variant = 'info',
  title,
  children,
  dismissible = false,
  onDismiss,
  action,
  className,
}: AlertBannerProps) {
  const [visible, setVisible] = useState(true);
  const { icon: Icon, bg, border, fg, iconColor } = VARIANT_STYLE[variant];

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <div
      role="alert"
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', className)}
      style={{ backgroundColor: bg, borderColor: border, color: fg }}
    >
      <Icon className="mt-0.5 size-5 shrink-0" style={{ color: iconColor }} />
      <div className="min-w-0 flex-1">
        {title && <p className="text-sm font-semibold">{title}</p>}
        {children && <div className={cn('text-sm', title && 'mt-0.5 text-content-secondary')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className="shrink-0 rounded p-0.5 text-content-muted transition-colors hover:bg-black/5 hover:text-content-primary"
          aria-label="Fermer l'alerte"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
